import { ButtonHTMLAttributes, Children, Fragment, isValidElement, ReactElement, ReactNode } from 'react';
import classnames from 'classnames';
import { FontIcon, FontIconName } from '../FontIcon';
import { Spinner } from '../Spinner';
import styles from './button.module.sass';
import parts from './parts.module.sass';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    children?: ReactNode;
    /** Figma Type=Primary */
    primary?: boolean;
    /** Figma Type=Black */
    secondary?: boolean;
    /** If `true` padding is reduced, making button smaller */
    compact?: boolean;
    small?: boolean;
    /** Adds styles for square button with icon only, no text */
    social?: boolean;
    round?: boolean;
    /** Transparent bg, `width: auto` */
    plain?: boolean;
    /** Allows the button to grow to the width of its container */
    fullWidth?: boolean;
    /**
     * Component (e.g. NextJS' `Link`) passed from application level and used to render link
     * This way Footer links do not depend on NextJS (or some other library) and can be configured from the parent
     */
    LinkComponent?: any;
    href?: string;
    to?: string;
    icon?: FontIconName;
    contactsButton?: boolean;
    /** Displays spinner inside the button; disables button */
    loading?: boolean;
    keepWidth?: boolean;
    target?: string;
    state?: any;
    white?: boolean;
}

const isText = (child: unknown): child is string | number => typeof child === 'string' || typeof child === 'number';
const INLINE = new Set(['span', 'b', 'strong', 'em', 'bdi']);

const textOf = (node: unknown): string | null => {
    if (isText(node)) return String(node);
    if (!isValidElement(node) || typeof node.type !== 'string' || !INLINE.has(node.type)) return null;
    const pieces = Children.toArray((node as ReactElement<{ children?: ReactNode }>).props.children).map(textOf);
    return pieces.every((piece) => piece !== null) ? pieces.join('') : null;
};

const rollLabel = (children: ReactNode) => {
    const flat = Children.toArray(children).flatMap((child) =>
        isValidElement(child) && child.type === Fragment
            ? Children.toArray((child as ReactElement<{ children?: ReactNode }>).props.children)
            : [child]
    );
    const isIcon = (child: unknown) => isValidElement(child) && child.type === FontIcon;
    const words = flat.filter((child) => !isIcon(child));
    const texts = words.map(textOf);
    const label = texts.join('');
    if (!label.trim() || texts.some((text) => text === null)) return children;

    let placed = false;
    return flat.map((child) => {
        if (isIcon(child)) return child;
        if (placed) return null;
        placed = true;
        return (
            <span key="roll" className={parts.roll}>
                <span className={parts.reel} data-label={label}>
                    {words}
                </span>
            </span>
        );
    });
};

export const Button = ({
    LinkComponent,
    children,
    disabled,
    primary,
    secondary,
    compact,
    social,
    small,
    round,
    plain,
    fullWidth,
    contactsButton,
    loading,
    keepWidth,
    icon,
    white,
    className,
    ...htmlProps
}: ButtonProps) => {
    const holdsSize = loading && keepWidth;
    const iconMarkup =
        icon && (!loading || keepWidth) ? (
            <div className={parts.iconWrapper}>
                <FontIcon name={icon} />
            </div>
        ) : null;

    const spinner = loading ? (
        <span className={parts.spinner}>
            <Spinner inline />
        </span>
    ) : null;

    const rolls = !plain;
    const content = (
        <>
            {(!loading || keepWidth) && (rolls ? rollLabel(children) : children)}
            {iconMarkup}
            {spinner}
        </>
    );

    const isDisabled = disabled || loading;
    const classNames = classnames(
        {
            [styles.compact]: compact,
            [styles.fullWidth]: fullWidth,
            [styles['contacts-button']]: contactsButton,
            [styles.social]: social,
            [styles.small]: small,
            [styles.round]: round,
            [styles.primary]: primary,
            [styles.secondary]: secondary,
            [styles.plain]: plain,
            [styles.white]: white,
            [parts.hold]: holdsSize,
        },
        styles.button,
        className
    );

    const props = {
        className: classNames,
        disabled: isDisabled,
        ...htmlProps,
    };

    return LinkComponent ? <LinkComponent {...props}>{content}</LinkComponent> : <button {...props}>{content}</button>;
};
